import type { RouteObject } from 'react-router-dom';
import { RootLayout } from '@shared/layouts/RootLayout';
import { AuthLayout } from '@shared/layouts/AuthLayout';
import { AppLayout } from '@shared/layouts/AppLayout';
import { ApplicationGuard, ProtectedRoute, PublicRoute } from '@shared/router/guards';
import { RouteErrorBoundary } from '@shared/router/RouteErrorBoundary';
import { RouteSplashScreen } from '@shared/router/RouteSplashScreen';
import { NotFoundRoute } from '@shared/router/NotFoundRoute';
import { LandingRoute } from '@shared/router/LandingRoute';
import { UnauthorizedPage } from '@shared/router/UnauthorizedPage';
import { LoginPage } from '@shared/auth/pages/LoginPage';
import { ForgotPasswordPage } from '@shared/auth/pages/ForgotPasswordPage';
import { ChangePasswordPage } from '@shared/auth/pages/ChangePasswordPage';
import { PATHS } from '@shared/router/paths';

export interface AppRouteOptions {
  /** Screens rendered inside the application shell (sidebar + top bar). */
  shellRoutes: RouteObject[];
  /** Authenticated screens rendered WITHOUT the shell (e.g. a print preview). */
  bareRoutes?: RouteObject[];
}

/**
 * The route tree every HEXAUNITY application uses. Authorization is layered,
 * never substituted:
 *
 *   RootLayout (+ error boundary)
 *   ├─ PublicRoute → AuthLayout             /login, /forgot-password (Identity)
 *   └─ ProtectedRoute                       1. authenticated (Identity session)
 *       └─ ApplicationGuard                 2. holds THIS application
 *           ├─ bare routes
 *           └─ AppLayout
 *               ├─ index → first permitted destination
 *               ├─ /change-password, /unauthorized
 *               └─ PermissionGuard(...)      3. per-route permission keys
 *                   └─ feature page
 *
 * Hiding a sidebar item does not protect a route, a route guard does not
 * protect data, and the backend re-checks every request regardless.
 */
export function buildAppRoutes({ shellRoutes, bareRoutes = [] }: AppRouteOptions): RouteObject[] {
  return [
    {
      element: <RootLayout />,
      errorElement: <RouteErrorBoundary />,
      // Shown while a deep link's screen chunk downloads on first load.
      hydrateFallbackElement: <RouteSplashScreen />,
      children: [
        {
          element: <PublicRoute />,
          children: [
            {
              element: <AuthLayout />,
              children: [
                { path: PATHS.auth.login, element: <LoginPage /> },
                { path: PATHS.auth.forgotPassword, element: <ForgotPasswordPage /> },
              ],
            },
          ],
        },
        {
          element: <ProtectedRoute />,
          children: [
            {
              element: <ApplicationGuard />,
              children: [
                ...bareRoutes,
                {
                  element: <AppLayout />,
                  children: [
                    { index: true, element: <LandingRoute /> },
                    { path: PATHS.auth.changePassword, element: <ChangePasswordPage /> },
                    { path: PATHS.app.unauthorized, element: <UnauthorizedPage /> },
                    ...shellRoutes,
                  ],
                },
              ],
            },
          ],
        },
        { path: PATHS.notFound, element: <NotFoundRoute /> },
      ],
    },
  ];
}
